<template>
  <div class="cronExpressionInputComponent">

    <!-- Mode selector -->
    <b-nav tabs class="mb-3">
      <b-nav-item :active="activeTab === 'preset'" @click="switchTab('preset')">Prédéfini</b-nav-item>
      <b-nav-item :active="activeTab === 'custom'" @click="switchTab('custom')">Personnalisé</b-nav-item>
    </b-nav>

    <!-- PRESET MODE -->
    <div v-if="activeTab === 'preset'">
      <b-form-group label="Type de planification">
        <b-form-radio-group
          v-model="presetType"
          :options="presetTypeOptions"
          stacked
          @change="onPresetTypeChange"
        />
      </b-form-group>

      <div v-if="presetType !== 'none'" class="row mt-2">
        <!-- Day of week (weekly only) -->
        <div v-if="presetType === 'weekly'" class="col-sm-6 col-12">
          <b-form-group label="Jour de la semaine">
            <b-form-select v-model="presetDow" :options="dowOptions" @change="emitValue" />
          </b-form-group>
        </div>
        <!-- Day of month (monthly / quarterly) -->
        <div v-if="presetType === 'monthly' || presetType === 'quarterly'" class="col-sm-6 col-12">
          <b-form-group label="Jour du mois">
            <b-form-select v-model="presetDom" :options="domOptions" @change="emitValue" />
          </b-form-group>
        </div>
        <!-- Hour -->
        <div class="col-sm-6 col-12">
          <b-form-group label="Heure">
            <b-form-select v-model="presetHour" :options="hourOptions" @change="emitValue" />
          </b-form-group>
        </div>
        <!-- Minute -->
        <div class="col-sm-6 col-12">
          <b-form-group label="Minute">
            <b-form-select v-model="presetMinute" :options="minuteOptions" @change="emitValue" />
          </b-form-group>
        </div>
      </div>
    </div>

    <!-- CUSTOM MODE -->
    <div v-if="activeTab === 'custom'">
      <p class="text-muted small mb-2">
        Format : <code>minute heure jour_du_mois mois jour_de_la_semaine</code><br>
        Utilisez <code>*</code> pour "toutes les valeurs", <code>*/n</code> pour "tous les n", un nombre pour une valeur précise, ou <code>3,4,5</code> pour une liste de valeurs.
      </p>
      <div class="row">
        <div class="col">
          <b-form-group label="Minute" label-class="small">
            <b-form-input
              v-model="customParts[0]"
              size="sm"
              :state="customParts[0].trim() !== '' ? null : false"
              @input="onCustomInput"
              @blur="fillEmptyWithStar(0)"
            />
            <small class="text-muted">0–59</small>
          </b-form-group>
        </div>
        <div class="col">
          <b-form-group label="Heure" label-class="small">
            <b-form-input
              v-model="customParts[1]"
              size="sm"
              :state="customParts[1].trim() !== '' ? null : false"
              @input="onCustomInput"
              @blur="fillEmptyWithStar(1)"
            />
            <small class="text-muted">0–23</small>
          </b-form-group>
        </div>
        <div class="col">
          <b-form-group label="Jour du mois" label-class="small">
            <b-form-input
              v-model="customParts[2]"
              size="sm"
              :state="customParts[2].trim() !== '' ? null : false"
              @input="onCustomInput"
              @blur="fillEmptyWithStar(2)"
            />
            <small class="text-muted">1–31</small>
          </b-form-group>
        </div>
        <div class="col">
          <b-form-group label="Mois" label-class="small">
            <b-form-input
              v-model="customParts[3]"
              size="sm"
              :state="customParts[3].trim() !== '' ? null : false"
              @input="onCustomInput"
              @blur="fillEmptyWithStar(3)"
            />
            <small class="text-muted">1–12</small>
          </b-form-group>
        </div>
        <div class="col">
          <b-form-group label="Jour semaine" label-class="small">
            <b-form-input
              v-model="customParts[4]"
              size="sm"
              :state="customParts[4].trim() !== '' ? null : false"
              @input="onCustomInput"
              @blur="fillEmptyWithStar(4)"
            />
            <small class="text-muted">0=dim, 1=lun…</small>
          </b-form-group>
        </div>
      </div>
      <b-alert v-if="hasEmptyCustomField" show variant="warning" class="py-1 px-2 small mb-0">
        Remplissez tous les champs (utilisez <code>*</code> pour "toutes les valeurs").
      </b-alert>
    </div>

    <!-- Result summary -->
    <div class="cron-result-box mt-2 p-2">
      <div class="d-flex align-items-center">
        <span class="text-muted small mr-2">Expression&nbsp;:</span>
        <code class="cron-code">{{ currentExpression || '(aucune)' }}</code>
        <b-button
          v-if="currentExpression && activeTab === 'custom'"
          size="sm"
          variant="link"
          class="ml-auto p-0"
          v-b-tooltip.hover title="Copier"
          @click="copyToClipboard"
        >
          📋
        </b-button>
      </div>
      <div class="mt-1">
        <small :class="humanDescriptionClass">
          <strong>{{ humanDescription }}</strong>
        </small>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'cronExpressionInputComponent',
  props: {
    value: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      activeTab: 'preset',
      // Preset mode state
      presetType: 'none',
      presetHour: 8,
      presetMinute: 0,
      presetDow: 1,
      presetDom: 1,
      // Custom mode state (5 cron fields)
      customParts: ['*', '*', '*', '*', '*'],
      initialLoad: true,

      presetTypeOptions: [
        { text: 'Non crawlable (désactivé)', value: 'none' },
        { text: 'Tous les jours', value: 'daily' },
        { text: 'Toutes les semaines', value: 'weekly' },
        { text: 'Tous les mois', value: 'monthly' },
        { text: 'Tous les trimestres (tous les 3 mois)', value: 'quarterly' },
      ],
      dowOptions: [
        { text: 'Lundi', value: 1 },
        { text: 'Mardi', value: 2 },
        { text: 'Mercredi', value: 3 },
        { text: 'Jeudi', value: 4 },
        { text: 'Vendredi', value: 5 },
        { text: 'Samedi', value: 6 },
        { text: 'Dimanche', value: 0 },
      ],
      domOptions: Array.from({ length: 28 }, (_, i) => ({ text: `${i + 1}`, value: i + 1 })),
      hourOptions: Array.from({ length: 24 }, (_, i) => ({ text: `${i}h`, value: i })),
      minuteOptions: [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(m => ({
        text: String(m).padStart(2, '0'),
        value: m
      })),
    }
  },
  computed: {
    currentExpression () {
      if (this.activeTab === 'custom') {
        const parts = this.customParts
        if (parts.some(p => !p.trim())) return ''
        return parts.join(' ')
      }
      // Preset mode
      const min = this.presetMinute
      const hour = this.presetHour
      switch (this.presetType) {
        case 'none':    return ''
        case 'daily':   return `${min} ${hour} * * *`
        case 'weekly':  return `${min} ${hour} * * ${this.presetDow}`
        case 'monthly': return `${min} ${hour} ${this.presetDom} * *`
        case 'quarterly': return `${min} ${hour} ${this.presetDom} */3 *`
        default:        return ''
      }
    },
    humanDescription () {
      return this.describeCron(this.currentExpression)
    },
    humanDescriptionClass () {
      return this.currentExpression ? 'text-success' : 'text-muted'
    },
    hasEmptyCustomField () {
      return this.activeTab === 'custom' && this.customParts.some(p => !p.trim())
    },
  },
  watch: {
    value: {
      immediate: true,
      handler (newVal) {
        this.loadFromExpression(newVal || '')
      }
    },
    currentExpression (newVal) {
      this.$emit('input', newVal)
      this.$emit('valid', !this.hasEmptyCustomField)
    },
    hasEmptyCustomField (val) {
      this.$emit('valid', !val)
    }
  },
  methods: {
    switchTab (tab) {
      if (tab === 'custom' && this.activeTab === 'preset') {
        // Sync current expression into custom fields
        const expr = this.currentExpression
        if (expr) {
          this.customParts = expr.split(' ')
        }
      }
      this.activeTab = tab
    },
    onPresetTypeChange () {
      this.emitValue()
    },
    emitValue () {
      this.$emit('input', this.currentExpression)
    },
    onCustomInput () {
      this.$emit('input', this.currentExpression)
      this.$emit('valid', !this.hasEmptyCustomField)
    },
    fillEmptyWithStar (index) {
      if (!this.customParts[index].trim()) {
        this.$set(this.customParts, index, '*')
      }
    },
    loadFromExpression (expr) {
      if (!expr) {
        if (this.initialLoad) {
          this.activeTab = 'preset'
          this.presetType = 'none'
        }
        this.initialLoad = false
        return
      }
      const detected = this.detectPreset(expr)
      if (this.initialLoad) {
        this.activeTab = detected.type === 'custom' ? 'custom' : 'preset'
      }
      this.initialLoad = false
      if (detected.type === 'custom') {
        this.customParts = expr.trim().split(/\s+/)
      } else {
        this.presetType = detected.type
        if (detected.hour !== undefined) this.presetHour = detected.hour
        if (detected.minute !== undefined) this.presetMinute = detected.minute
        if (detected.dow !== undefined) this.presetDow = detected.dow
        if (detected.dom !== undefined) this.presetDom = detected.dom
      }
    },
    detectPreset (expr) {
      if (!expr) return { type: 'none' }
      const parts = expr.trim().split(/\s+/)
      if (parts.length !== 5) return { type: 'custom' }
      const [min, hour, dom, month, dow] = parts
      const minN = parseInt(min), hourN = parseInt(hour)
      const isSimpleNum = s => /^\d+$/.test(s)

      // Daily: "min hour * * *"
      if (dom === '*' && month === '*' && dow === '*' && isSimpleNum(min) && isSimpleNum(hour)) {
        return { type: 'daily', hour: hourN, minute: minN }
      }
      // Weekly: "min hour * * dow"
      if (dom === '*' && month === '*' && isSimpleNum(dow) && isSimpleNum(hour) && isSimpleNum(min)) {
        return { type: 'weekly', hour: hourN, minute: minN, dow: parseInt(dow) }
      }
      // Monthly: "min hour dom * *"
      if (isSimpleNum(dom) && month === '*' && dow === '*' && isSimpleNum(hour) && isSimpleNum(min)) {
        return { type: 'monthly', hour: hourN, minute: minN, dom: parseInt(dom) }
      }
      // Quarterly: "min hour dom */3 *"
      if (isSimpleNum(dom) && month === '*/3' && dow === '*' && isSimpleNum(hour) && isSimpleNum(min)) {
        return { type: 'quarterly', hour: hourN, minute: minN, dom: parseInt(dom) }
      }
      return { type: 'custom' }
    },
    describeCron (expr) {
      if (!expr) return 'Ce dossier ne sera pas crawlé automatiquement.'
      const parts = expr.trim().split(/\s+/)
      if (parts.length !== 5) return `Expression cron : ${expr}`
      const [min, hour, dom, month, dow] = parts
      const days = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi']
      const timeStr = `${hour}h${String(min).padStart(2, '0')}`
      const isNum = s => /^\d+$/.test(s)

      if (dom === '*' && month === '*' && dow === '*' && isNum(hour) && isNum(min)) {
        return `⏰ Tous les jours à ${timeStr}`
      }
      if (dom === '*' && month === '*' && isNum(dow) && isNum(hour) && isNum(min)) {
        return `⏰ Chaque ${days[parseInt(dow)] || `jour ${dow}`} à ${timeStr}`
      }
      if (isNum(dom) && month === '*' && dow === '*' && isNum(hour) && isNum(min)) {
        return `⏰ Le ${dom} de chaque mois à ${timeStr}`
      }
      if (isNum(dom) && month === '*/3' && dow === '*' && isNum(hour) && isNum(min)) {
        return `⏰ Le ${dom} tous les 3 mois à ${timeStr}`
      }
      return `⏰ Expression cron : ${expr}`
    },
    copyToClipboard () {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(this.currentExpression)
      }
    }
  }
}
</script>

<style scoped>
.cron-result-box {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}
.cron-code {
  font-size: 0.9em;
  background: #e9ecef;
  padding: 2px 6px;
  border-radius: 3px;
}
</style>
